import { useState, useEffect } from 'react';
import api, { handleApiError } from '../utils/api';
import { isWireTransferEnabled } from '../utils/featureFlags';

/**
 * Reusable payment processing hook
 * Handles credit card payments, 3D Secure redirects, wire transfers, and in-place payments
 */
const usePaymentProcessor = (paymentOptions, onSuccess, onError) => {
  // Payment state
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [redirectData, setRedirectData] = useState(null);

  // Credit card form state
  const [creditCardForm, setCreditCardForm] = useState({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardHolderName: ''
  });

  // Wire transfer state
  const [wireTransferInfo, setWireTransferInfo] = useState(null);

  // In-place payment state
  const [inPlaceToken, setInPlaceToken] = useState(null);

  // Listen for payment results from iframe (3D Secure)
  useEffect(() => {
    const handlePaymentMessage = (event) => {
      console.log('Received message from payment iframe:', event.data);
      
      if (event.data && event.data.type === 'payment_result') {
        setRedirectData(null);
        setActionLoading(false);
        
        if (event.data.success) {
          setPaymentResult({
            payment: {
              paymentToken: event.data.paymentToken,
              transactionId: event.data.transactionId,
              status: 'completed'
            }
          });
          
          if (onSuccess) {
            onSuccess(event.data.message || 'Payment completed successfully! Your membership is now active.');
          }
          
          // Close dialog after success - reduced timeout since backend now has manual close
          setTimeout(() => {
            setDialogOpen(false);
            resetPaymentState();
          }, 1000);
        } else {
          if (onError) {
            onError(event.data.error || 'Payment failed');
          }
          console.error('Payment failed:', event.data);
        }
      }
    };

    window.addEventListener('message', handlePaymentMessage);
    
    return () => {
      window.removeEventListener('message', handlePaymentMessage);
    };
  }, [onSuccess, onError]);

  // Handle payment method selection
  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    
    if (method === 'credit_card') {
      setDialogOpen(true);
    } else if (isWireTransferEnabled() && method === 'wire_transfer') {
      handleWireTransfer();
    } else if (method === 'in_place') {
      handleInPlacePayment();
    }
  };

  // Handle wire transfer payment
  const handleWireTransfer = async () => {
    setActionLoading(true);
    
    try {
      const response = await api.post('/payment/wire-transfer', {
        amount: paymentOptions.registration.amount
      });
      
      if (response.data.success) {
        setWireTransferInfo(response.data.data.payment.wireTransferInstructions);
        setDialogOpen(true);
        if (onSuccess) {
          onSuccess('Wire transfer instructions generated successfully');
        }
      }
    } catch (error) {
      console.error('Wire transfer error:', error);
      if (onError) {
        onError(handleApiError(error));
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Handle in-place payment
  const handleInPlacePayment = async () => {
    setActionLoading(true);
    
    try {
      const response = await api.post('/payment/in-place', {
        amount: paymentOptions.registration.amount
      });
      
      if (response.data.success) {
        setInPlaceToken(response.data.data.payment);
        setDialogOpen(true);
        if (onSuccess) {
          onSuccess('In-place payment token generated successfully');
        }
      }
    } catch (error) {
      console.error('In-place payment error:', error);
      if (onError) {
        onError(handleApiError(error));
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Handle credit card payment
  const handleCreditCardPayment = async () => {
    setActionLoading(true);
    
    try {
      const response = await api.post('/payment/credit-card', {
        amount: paymentOptions.registration.amount,
        cardDetails: creditCardForm
      });
      
      if (response.data.success) {
        if (response.data.requiresRedirect && response.data.redirectData) {
          // Gateway requires 3D Secure or similar redirect (e.g., Payten)
          console.log('Payment requires redirect (3D Secure)');
          setRedirectData(response.data.redirectData);
          setDialogOpen(true);
          // Don't set actionLoading to false - wait for iframe result
        } else {
          // Direct payment success (e.g., Test Gateway)
          setPaymentResult(response.data.data);
          
          if (onSuccess) {
            onSuccess(response.data.data.message || 'Payment completed successfully! Your membership is now active.');
          }
          
          setActionLoading(false);
        }
      } else {
        throw new Error(response.data.error || 'Payment failed');
      }
    } catch (error) {
      console.error('Credit card payment error:', error);
      const errorMessage = handleApiError(error);

      // Show validation errors if available
      if (error.response?.data?.details?.errors) {
        const validationErrors = error.response.data.details.errors
          .map(err => `${err.field}: ${err.message}`)
          .join(', ');
        if (onError) {
          onError(`Validation failed: ${validationErrors}`);
        }
      } else {
        if (onError) {
          onError(errorMessage);
        }
      }
      
      setActionLoading(false);
    }
  };

  // Utility functions
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setCreditCardForm(prev => ({
      ...prev,
      cardNumber: formatted.replace(/\s/g, '')
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      if (onSuccess) {
        onSuccess('Copied to clipboard');
        setTimeout(() => onSuccess(''), 2000);
      }
    });
  };

  // Reset functions
  const resetPaymentState = () => {
    setSelectedMethod('');
    setDialogOpen(false);
    setPaymentResult(null);
    setRedirectData(null);
    setWireTransferInfo(null);
    setInPlaceToken(null);
    setCreditCardForm({
      cardNumber: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      cardHolderName: ''
    });
  };

  return {
    // State
    actionLoading,
    selectedMethod,
    dialogOpen,
    setDialogOpen,
    paymentResult,
    redirectData,
    creditCardForm,
    setCreditCardForm,
    wireTransferInfo,
    inPlaceToken,

    // Handlers
    handleMethodSelect,
    handleCreditCardPayment,
    handleWireTransfer,
    handleInPlacePayment,
    handleCardNumberChange,
    copyToClipboard,
    resetPaymentState,

    // Utilities
    formatCardNumber
  };
};

export default usePaymentProcessor;